import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, X, Calendar, Building, Send, CheckCircle2, MessageSquare, AlertCircle, RefreshCw } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

const AdminContacts = () => {
  const { api } = useAdminAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [replyFeedback, setReplyFeedback] = useState(null);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await api().get("/admin/contacts");
      setContacts(res.data);
    } catch (err) {
      console.error("Error loading contacts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [api]);

  const handleUpdateStatus = async (contactId, status) => {
    try {
      const res = await api().put(`/admin/contacts/${contactId}/status`, { status });
      if (selected && selected.id === contactId) {
        setSelected(res.data.contact);
      }
      fetchContacts();
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!selected || !replyMessage.trim()) return;
    setSendingReply(true);
    setReplyFeedback(null);
    try {
      const res = await api().post(`/admin/contacts/${selected.id}/reply`, {
        reply_message: replyMessage
      });
      setReplyFeedback({ type: "success", text: "Reply dispatched successfully via Microsoft Graph!" });
      setSelected(res.data.contact);
      setReplyMessage("");
      fetchContacts();
    } catch (err) {
      setReplyFeedback({ type: "error", text: err.response?.data?.detail || "Failed to send reply" });
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-light text-white">Contact Messages</h1>
          <p className="text-sm text-cyan-300/60 mt-1">Inbound corporate inquiries from the website contact center</p>
        </div>
        <button
          onClick={fetchContacts}
          className="p-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-cyan-300 text-xs flex items-center gap-2 transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && <div className="text-white/40 col-span-2 text-center py-12">Loading inquiries...</div>}
        {!loading && contacts.length === 0 && (
          <div className="p-12 rounded-2xl border border-white/10 text-center bg-black/20 text-white/40 col-span-2">
            No contact messages recorded yet.
          </div>
        )}
        {!loading && contacts.map((c) => {
          const name = c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Enquirer';
          return (
            <motion.div
              key={c.id}
              whileHover={{ y: -2 }}
              onClick={() => { setSelected(c); setReplyFeedback(null); }}
              className="p-5 rounded-2xl border border-white/10 cursor-pointer hover:border-cyan-400/30 transition-colors relative overflow-hidden"
              style={{ background: "rgba(10,22,40,0.5)", backdropFilter: "blur(12px)" }}
              data-testid={`contact-card-${c.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium">{name}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-mono tracking-wider border ${
                      c.status === 'replied' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' :
                      c.status === 'read' ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300' :
                      'bg-amber-500/10 border-amber-500/30 text-amber-300'
                    }`}>
                      {c.status || 'new'}
                    </span>
                  </div>
                  {c.organisation_name && (
                    <div className="text-xs text-white/60 flex items-center gap-1 mt-0.5">
                      <Building className="w-3 h-3 text-cyan-400" /> {c.organisation_name}
                    </div>
                  )}
                  <div className="text-cyan-300/70 text-xs flex items-center gap-1 mt-1">
                    <Mail className="w-3 h-3" />{c.email}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-cyan-300 font-mono">{c.reference_number || c.id}</div>
                  <div className="text-white/40 text-[11px] flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3" />
                    {c.date || (c.created_at ? new Date(c.created_at).toLocaleDateString() : "")}
                  </div>
                </div>
              </div>
              <div className="text-xs font-medium text-white/90 mb-1">{c.subject || "Corporate Enquiry"}</div>
              <p className="text-white/70 text-xs line-clamp-2">{c.message}</p>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="fixed top-0 right-0 h-full w-full max-w-2xl z-50 overflow-y-auto"
              style={{ background: "linear-gradient(180deg, #0a1628 0%, #050a14 100%)", borderLeft: "1px solid rgba(255,255,255,0.1)" }}
              data-testid="contact-detail-drawer"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-white/10" style={{ background: "rgba(10,22,40,0.95)", backdropFilter: "blur(20px)" }}>
                <div>
                  <div className="text-xs text-cyan-300 font-mono">{selected.reference_number || selected.id}</div>
                  <h2 className="text-xl font-light text-white">Contact Message Details</h2>
                </div>
                <button onClick={() => setSelected(null)} className="text-white/60 hover:text-white p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {replyFeedback && (
                  <div className={`p-4 rounded-xl border text-sm flex items-center gap-2 ${replyFeedback.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
                    {replyFeedback.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    <span>{replyFeedback.text}</span>
                  </div>
                )}

                <div className="p-4 rounded-xl bg-black/40 border border-white/10 flex items-center justify-between">
                  <div className="text-xs text-white/60 uppercase tracking-wider">Current Status</div>
                  <div className="flex gap-2">
                    {['new', 'read', 'replied', 'closed'].map((st) => (
                      <button
                        key={st}
                        onClick={() => handleUpdateStatus(selected.id, st)}
                        className={`px-3 py-1 rounded text-xs uppercase font-mono tracking-wider transition-all border ${
                          selected.status === st
                            ? "bg-cyan-400/20 text-cyan-300 border-cyan-400/40"
                            : "bg-white/5 text-white/60 border-white/10 hover:border-white/20"
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-black/40 border border-white/5 text-xs">
                  <div>
                    <div className="text-cyan-300/60 uppercase tracking-wider mb-1">Full Name</div>
                    <div className="text-white font-medium text-sm">{selected.full_name || `${selected.first_name || ''} ${selected.last_name || ''}`}</div>
                  </div>
                  <div>
                    <div className="text-cyan-300/60 uppercase tracking-wider mb-1">Organisation</div>
                    <div className="text-white">{selected.organisation_name || "N/A"}</div>
                  </div>
                  <div>
                    <div className="text-cyan-300/60 uppercase tracking-wider mb-1">Official Email</div>
                    <div className="text-cyan-300">{selected.email}</div>
                  </div>
                  <div>
                    <div className="text-cyan-300/60 uppercase tracking-wider mb-1">Submission Date / Time</div>
                    <div className="text-white">{selected.date || new Date(selected.created_at).toLocaleDateString()} {selected.time || ''}</div>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-cyan-300/60 mb-2">Subject / Purpose</div>
                  <div className="text-white font-medium text-sm p-3 rounded-lg bg-black/30 border border-white/5">
                    {selected.subject || "Corporate Enquiry"}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-cyan-300/60 mb-2">Message Body</div>
                  <div className="p-4 rounded-xl bg-black/60 border border-white/10 text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
                    {selected.message}
                  </div>
                </div>

                {/* Direct Email Reply Composer */}
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-cyan-400" /> Dispatch Direct Corporate Response
                  </h3>
                  <form onSubmit={handleSendReply} className="space-y-3">
                    <textarea
                      rows={5}
                      placeholder={`Write a formal response to ${selected.full_name || 'the enquirer'}...`}
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      className="w-full p-3 rounded-xl border border-white/10 bg-black/60 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/50"
                    />
                    <button
                      type="submit"
                      disabled={sendingReply || !replyMessage.trim()}
                      className="w-full py-3 rounded-xl font-medium uppercase tracking-wider text-sm flex items-center justify-center gap-2 text-black hover:opacity-90 disabled:opacity-50 transition-all"
                      style={{ background: "linear-gradient(135deg, #00FFD1 0%, #00a884 100%)" }}
                      data-testid="reply-email-button"
                    >
                      <Send className="w-4 h-4" />
                      <span>{sendingReply ? "Dispatching Reply..." : "Send Official Response via Graph/SMTP"}</span>
                    </button>
                  </form>
                </div>

                {/* Reply History */}
                {selected.reply_history && selected.reply_history.length > 0 && (
                  <div className="border-t border-white/10 pt-4">
                    <div className="text-xs uppercase tracking-wider text-cyan-300/60 mb-2">Reply History</div>
                    <div className="space-y-2">
                      {selected.reply_history.map((r, i) => (
                        <div key={i} className="p-3 rounded-lg bg-black/40 border border-white/5 text-xs text-white/70">
                          <div className="flex justify-between text-cyan-300 mb-1">
                            <span>{r.subject}</span>
                            <span className="text-white/40">{new Date(r.sent_at).toLocaleString()}</span>
                          </div>
                          <p className="whitespace-pre-wrap text-white/60">{r.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminContacts;
